import { useEffect, useState } from 'react';
import { montagePreselect } from '../../post-publish/lib/publishPlan';
import { loadLastAccountSet } from '../lib/groups';
import { usePublishDraft } from './publishDraft';

/**
 * Отмеченные аккаунты формы публикации — из общего черновика (рилс ↔ пост).
 *
 * Один раз за заход на экран ставим набор по умолчанию:
 *  - ролик из «Монтажа» — аккаунт самого ролика (одна публикация = один аккаунт);
 *  - иначе — последний набор человека («как в прошлый раз»), с кнопкой «Сбросить».
 */
export function useDraftAccounts({ userId, activeIds, montage }: {
  userId: string | null;
  /** id аккаунтов, которые в форме вообще можно отметить. */
  activeIds: readonly string[];
  montage: { active: boolean; resolved: boolean; itemAccountId: string | null; projectAccountIds: readonly string[] };
}) {
  const checked = usePublishDraft((s) => s.checked);
  const setChecked = usePublishDraft((s) => s.setChecked);
  const appliedLast = usePublishDraft((s) => s.appliedLast);
  const [lastIds, setLastIds] = useState<string[]>([]);
  useEffect(() => { setLastIds(loadLastAccountSet(userId)); }, [userId]);

  const activeKey = activeIds.join(',');
  useEffect(() => {
    const st = usePublishDraft.getState();
    if (st.initialized || activeIds.length === 0) return;
    const available = new Set(activeIds);
    if (montage.active) {
      if (!montage.resolved) return;
      st.setChecked(new Set(montagePreselect(montage.itemAccountId, montage.projectAccountIds, available)));
      st.setInitialized(true);
      return;
    }
    if (!userId) return;
    const last = loadLastAccountSet(userId).filter((id) => available.has(id));
    // Человек уже успел что-то отметить сам — его выбор важнее.
    if (st.checked.size === 0 && last.length) {
      st.setChecked(new Set(last));
      st.setAppliedLast(true);
    }
    st.setInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, userId, montage.active, montage.resolved, montage.itemAccountId]);

  /** «Сбросить» набор, поставленный по умолчанию. */
  const resetToEmpty = () => {
    setChecked(new Set());
    usePublishDraft.getState().setAppliedLast(false);
  };

  /** После публикации: набор стал «последним», галочки остаются. */
  const refreshLast = () => setLastIds(loadLastAccountSet(userId));

  return { checked, setChecked, lastIds, appliedLast, resetToEmpty, refreshLast };
}
